import { SelectionModel } from '@angular/cdk/collections';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { cloneDeep, isEmpty, uniq } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, filter, finalize, take } from 'rxjs/operators';

import { UserService } from 'src/app/core/api/user.service';
import { DialogYesNoComponent } from 'src/app/shared/dialogs/dialog-yes-no/dialog-yes-no.component';

import { CohortService } from '../../../core/api/cohort.service';
import { PaymentService } from '../../../core/api/payment.service';
import { RegionService } from '../../../core/api/region.service';
import { SiteService } from '../../../core/api/site.service';
import { UtilitiesService } from '../../../core/api/utlities.service';
import { CsvService } from '../../../core/helper/csv.service';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { DialogChangeSiteStateComponent } from '../../../shared/components/dialog-change-site-state/dialog-change-site-state.component';
import { DialogConfirmPaymentComponent } from '../../../shared/dialogs/dialog-confirm-payment/dialog-confirm-payment.component';
import { Cohort, Region, Site, User } from '../../../shared/models';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import * as fromApp from '../../../store';
import { setDashboardFilter, setDashboardState } from '../../../store/admin/admin.actions';
import { selectAdminFiltersAndPage } from '../../../store/admin/admin.selectors';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { AdminTableBase } from '../admin-table-base/admin-table-base';
import { Dashboard } from "../../../shared/models/dashboard.model";
import { DashboardService } from "../../../core/api/dashboard.service";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { DialogChangeDashboardNameComponent } from "../../../shared/dialogs/dialog-change-dashboard-name/dialog-change-dashboard-name.component";
import { StatusCategoryEnum } from "../../../shared/constants/site_status_category.enum";

export interface Column {
  name: string;
  value: string;
}

@Component({
  selector: 'fc-site-dashboard',
  templateUrl: './site-dashboard.component.html',
  styleUrls: ['./site-dashboard.component.scss'],
})
export class SiteDashboardComponent extends AdminTableBase implements OnInit {
  destroyRef = inject(DestroyRef);
  math = Math;
  readonly PAGE_NAME: string = 'sites';

  displayedColumns: string[] = [
    'select',
    'needsReview',
    'updated_at',
    'submittedDate',
    'approvedDate',
    'status',
    'id',
    'cohort',
    'name',
    'accountId',
    'stripeStatus',
    'delegate',
    'region',
    'area',
    'valuation',
  ];

  statuses = [];

  defaultSortQuery = '&sort=updated_at(desc)';
  sortQuery = this.defaultSortQuery;
  // names of columns must match backend column names
  sortBy = {
    submitted_date: this.sortStates.untouched,
    updated_at: this.sortStates.untouched,
    approved_date: this.sortStates.untouched,
    status: this.sortStates.untouched,
    id: this.sortStates.untouched,
    name: this.sortStates.untouched,
    area: this.sortStates.untouched,
  };

  dataSource: MatTableDataSource<Site> = new MatTableDataSource<Site>();
  selection = new SelectionModel<Site>(true, []);

  enableBulkSiteStateChangeButton = false;
  enableBulkPaySiteButton = false;
  enableBulkStripeEmailButton = false;
  enableBulkCsvExport = false;

  sites: Array<Site>;
  regions: Array<Region>;
  cohorts: Array<Cohort>;
  admins: Array<User>;

  ownerQuery = '';
  hasStripeQuery = '';
  needsReviewQuery = '';
  delegateQuery = '';

  showTerminatedQuery = '';
  defaultFilterValues = {
    name: '',
    id: '',
    owner: '',
    delegate: '',
    status: [],
    cohort_id: null,
    region: null,
    show_terminated: false,
    assigned_to_me: false,
    buyer: null,
    user_has_stripe: null,
    assigned_admin_id: null,
    needs_review: null,
  };
  filterValues = cloneDeep(this.defaultFilterValues);
  editMode = false;

  isLimitedAdmin = false;
  canPaySites = false;

  dashboards: Dashboard[] = [];
  currentDashboardId = null;
  currentDashboard: Dashboard = null;

  applyFilterStatusSubject$: Subject<{ value: any }> = new Subject();
  applyFilterHasStripeSubject$: Subject<{ event: Event; column: string }> = new Subject();
  applyFilterNeedsReviewSubject$: Subject<{ event: Event; column: string }> = new Subject();
  applyFilterShowTerminatedSubject$: Subject<{ event: Event; column: string }> = new Subject();
  getSiteByNestedFieldSubject$: Subject<{ event: Event; field: string }> = new Subject();
  applyFilterAssignedToMeSubject$: Subject<{ event: Event; column: string }> = new Subject();

  allColumns: Column[] = [
    { value: 'area', name: 'Acreage' },
    { value: 'valuation', name: 'Annual Payment' },
    { value: 'accountId', name: 'Applicant' },
    { value: 'approvedDate', name: 'Approved Date' },
    { value: 'cohort', name: 'Cohort' },
    { value: 'delegate', name: 'Delegate' },
    { value: 'id', name: 'ID' },
    { value: 'needsReview', name: 'Needs Review' },
    { value: 'region', name: 'Region' },
    { value: 'select', name: 'Select Box' },
    { value: 'name', name: 'Site Name' },
    { value: 'status', name: 'Site Status' },
    { value: 'stripeStatus', name: 'Stripe Status' },
    { value: 'submittedDate', name: 'Submitted Date' },
    { value: 'updated_at', name: 'Updated Date' },
  ];

  constructor(
    public dialog: MatDialog,
    readonly siteService: SiteService,
    readonly csvService: CsvService,
    readonly siteHelper: SiteHelperService,
    readonly formatService: FormatHelperService,
    readonly regionService: RegionService,
    readonly utilitiesService: UtilitiesService,
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly userService: UserService,
    readonly cohortService: CohortService,
    readonly paymentService: PaymentService,
    readonly spinnerService: SpinnerService,
    readonly dashboardService: DashboardService
  ) {
    super();
    this.regionService.getAllRegions().subscribe(allRegions => {
      this.regions = allRegions;
    });
    this.cohortService.getAllCohorts().subscribe(allCohorts => {
      this.cohorts = allCohorts;
    });
  }

  ngOnInit() {
    this.applyFilterStatusSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((eventObj: { value: any }) => {
      this.doGetSiteByStatusField(eventObj.value);
    });

    this.applyFilterHasStripeSubject$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        const value = eventObj.event.value === undefined ? null : String(eventObj.event.value);
        this.doGetSiteByHasStripeField(value, eventObj.column);
      });

    this.applyFilterNeedsReviewSubject$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        const value = eventObj.event.value === undefined ? null : String(eventObj.event.value);
        this.doGetSiteByNeedsReviewField(value, eventObj.column);
      });

    this.applyFilterShowTerminatedSubject$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        const value = eventObj.event.checked.toString();
        this.doGetTerminatedSites(value, eventObj.column);
      });

    this.applyFilterAssignedToMeSubject$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        const value = eventObj.event.checked.toString();
        this.doGetMySites(value, eventObj.column);
      });

    this.getSiteByNestedFieldSubject$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: Event; field: string }) => {
        this.doGetSiteByNestedField(eventObj.event, eventObj.field);
      });

    this.selection.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.selection.isEmpty()) {
        this.enableBulkSiteStateChangeButton = false;
        this.enableBulkPaySiteButton = false;
        this.enableBulkStripeEmailButton = false;
        this.enableBulkCsvExport = false;
      } else {
        this.enableBulkSiteStateChangeButton = this.selection.selected.every(
          (val, i, arr) => val.status === arr[0].status
        );
        this.enableBulkPaySiteButton = this.siteHelper.sitesArePayable(this.selection.selected) && this.canPaySites;
        this.enableBulkStripeEmailButton =
          this.selection.selected.every(
            (val, i, arr) => val.cohort.buyer && !val.account.user.hasStripe(val.cohort.buyer.id)
          ) && !this.isLimitedAdmin;
        this.enableBulkCsvExport = true;
      }
    });

    this.store
      .select(selectCurrentUser)
      .pipe(
        filter(currentUser => currentUser !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(currentUser => {
        this.isLimitedAdmin = currentUser.isLimitedAdmin;
        this.statuses = this.siteHelper.siteStatusList;
        this.canPaySites = currentUser.canPaySites;
      });

    this.store.select(selectAdminFiltersAndPage).pipe(take(1)).subscribe(state => {
      this.dashboardService.getDashboards(this.PAGE_NAME).subscribe((dashboards: Dashboard[]) => {
        this.dashboards = dashboards;
        this.setDefaultDashboard();
        if (this.currentDashboardId) {
          this.updateDashboardView();
        } else {
          this.resetAdminFilters(state.filters);
          this.queryItems();
        }
        this.initializeFilters();
      });
    });
    this.userService.getAdmins().subscribe((admins: User[]) => (this.admins = admins));
  }

  setDefaultDashboard() {
    this.currentDashboardId = this.dashboards.find(d => d.isDefault && d.ownerId !== null)?.id;
    if (!this.currentDashboardId) {
      this.currentDashboardId = this.dashboards.find(d => d.isDefault)?.id;
    }
  }

  resetAdminFilters(state: any): void {
    this.filterValues = cloneDeep(this.defaultFilterValues);
    let pageFilter = super.setUpAdminFilter(state);
    this.ownerQuery = pageFilter?.ownerQuery || '';
    this.delegateQuery = pageFilter?.delegateQuery || '';
    this.hasStripeQuery = pageFilter?.hasStripeQuery || '';
    this.needsReviewQuery = pageFilter?.needsReviewQuery || '';
    this.showTerminatedQuery = pageFilter?.showTerminatedQuery || '';
    // set up the filter display values

    this.filterValues.user_has_stripe = this.hasStripeQuery !== '' ? this.hasStripeQuery.includes('true') : null;
    this.filterValues.needs_review = this.needsReviewQuery !== '' ? this.needsReviewQuery.includes('true') : null;
    this.filterValues.owner = this.ownerQuery !== '' ? /contains,(.*)\)/.exec(this.ownerQuery)[1] : '';
    this.filterValues.delegate = this.delegateQuery !== '' ? /contains,(.*)\)/.exec(this.delegateQuery)[1] : '';
    this.filterValues.show_terminated = this.showTerminatedQuery !== '' ? this.showTerminatedQuery.includes('true') : false;
    this.changeFilterValueToNum('cohort_id');
  }

  getFullQueryString(): string {
    return (
      this.getBaseQueryString() +
      this.filterQuery +
      this.ownerQuery +
      this.delegateQuery +
      this.statusQuery +
      this.hasStripeQuery +
      this.needsReviewQuery +
      this.sortQuery +
      this.regionQuery +
      this.showTerminatedQuery +
      this.showAssignedToMeQuery +
      this.assignedAdminQuery
    );
  }

  applyStatusFilter(): void {
    this.applyFilterStatusSubject$.next({ value: this.filterValues.status });
  }

  applyHasStripeFilter(event: any, column: string): void {
    this.applyFilterHasStripeSubject$.next({ event, column });
  }

  applyNeedsReviewFilter(event: any, column: string): void {
    this.applyFilterNeedsReviewSubject$.next({ event, column });
  }

  applyShowTerminatedFilter(event: any, column: string): void {
    this.applyFilterShowTerminatedSubject$.next({ event, column });
  }

  applyAssignedToMeFilter(event: any, column: string): void {
    this.applyFilterAssignedToMeSubject$.next({ event, column });
  }

  getSiteByNestedField(event: Event, field: string): void {
    this.getSiteByNestedFieldSubject$.next({ event, field });
  }

  queryItems(): void {
    // always include pagination
    this.spinnerService.show('app-spinner');
    this.siteService
      .getFilteredSites(this.getFullQueryString())
      .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .subscribe(siteResults => {
        this.clearSelection();
        this.sites = siteResults.results;
        this.totalItems = siteResults.total;
        this.setAllPages();
        this.setStartEndIndexes();
        this.dataSource = new MatTableDataSource(this.sites);
      });
  }

  doGetTerminatedSites(value: string, field: string): void {
    if (!isEmpty(value)) {
      const filterStr = value.length !== 0 ? `&${field}=${value}` : '';
      if (field === 'show_terminated') {
        this.showTerminatedQuery = filterStr;
      }
    } else {
      this.showTerminatedQuery = '';
    }

    this.store.dispatch(
      setDashboardFilter({
        filter: 'showTerminatedQuery',
        value: this.showTerminatedQuery,
        page: this.PAGE_NAME,
      })
    );

    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }

  doGetSiteByNeedsReviewField(value: string, field: string): void {
    if (!isEmpty(value)) {
      const filterStr = value.length !== 0 ? `&${field}=${value}` : '';
      if (field === 'needs_review') {
        this.needsReviewQuery = filterStr;
      }
    } else {
      this.needsReviewQuery = '';
    }

    this.store.dispatch(
      setDashboardFilter({
        filter: 'needsReviewQuery',
        value: this.needsReviewQuery,
        page: this.PAGE_NAME,
      })
    );

    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }

  doGetSiteByHasStripeField(value: string, field: string): void {
    if (!isEmpty(value)) {
      const filterStr = value.length !== 0 ? `&${field}=${value}` : '';
      if (field === 'user_has_stripe') {
        this.hasStripeQuery = filterStr;
      }
    } else {
      this.hasStripeQuery = '';
    }

    this.store.dispatch(
      setDashboardFilter({
        filter: 'hasStripeQuery',
        value: this.hasStripeQuery,
        page: this.PAGE_NAME,
      })
    );

    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }

  doGetSiteByNestedField(event: Event | MatSelectChange, field: string): void {
    const rawFilterValue = ((event as Event).target as HTMLInputElement).value;

    // must encode value if we want to support special characters like ; # &
    const filterValue: string = this.utilitiesService.preprocessFilterString(rawFilterValue);

    const filterStr = filterValue.length !== 0 ? `&${field}=last_name(contains,${filterValue})` : '';

    if (field === 'owner') {
      this.ownerQuery = filterStr;
      this.store.dispatch(
        setDashboardFilter({
          filter: 'ownerQuery',
          value: filterStr,
          page: this.PAGE_NAME,
        })
      );
    } else if (field === 'delegate') {
      this.delegateQuery = filterStr;
      this.store.dispatch(
        setDashboardFilter({
          filter: 'delegateQuery',
          value: filterStr,
          page: this.PAGE_NAME,
        })
      );
    }

    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }

  onBulkCsvExport(): void {
    let csv_string =
      'Site ID,Status,Site Name,Site Created, Submitted Date,Approved Date,Active Date,Cohort Name,Region Name, ' +
      'Area, Annual Valuation, Net ERT,Assigned Admin,Current Step, Forestry Firm,' +
      'Applicant ID,Applicant Email,Applicant Name, Address1, Address2, City, State, Zip, Phone, Delegate Name, Stripe Status\n';
    this.dataSource.data.forEach(row => {
      if (this.selection.isSelected(row)) {
        csv_string +=
          '"' +
          row.id +
          '","' +
          row.status +
          '","' +
          row.name +
          '","' +
          (row.createDate || '') +
          '","' +
          (row.submittedDate || '') +
          '","' +
          (row.approvedDate || '') +
          '","' +
          (row.activationDate || '') +
          '","' +
          (row.cohort?.name || '') +
          '","' +
          (row.cohort?.region?.name || '') +
          '","' +
          Math.round(row.area) +
          '","' +
          this.getValuationDisplay(row) +
          '","' +
          (row.submittedDate !== null ? row.netErt || '' : '') +
          '","' +
          (row.assignedAdmin?.getDisplayName() || '') +
          '","' +
          (row.currentStep || '') +
          '","' +
          (row.forestryFirm || '') +
          '","' +
          (row.account.user?.id || '') +
          '","' +
          (row.account.user?.email || '') +
          '","' +
          (row.account.user?.getDisplayName() || '') +
          '","' +
          (row.account.user?.street1 || '') +
          '","' +
          (row.account.user?.street2 || '') +
          '","' +
          (row.account.user?.city || '') +
          '","' +
          (row.account.user?.state || '') +
          '","' +
          (row.account.user?.zip || '') +
          '","' +
          (row.account.user?.phone || '') +
          '","' +
          (row.account.user?.delegate?.getDisplayName() || '') +
          '","' +
          (row.account.user?.hasStripe(row.cohort.buyerId) || '') +
          '"\n';
      }
    });
    this.csvService.exportCSV(csv_string, 'Sites');
  }

  onBulkChangeSiteState(): void {
    const dialogRef = this.dialog.open(DialogChangeSiteStateComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        categories: this.isLimitedAdmin ? [StatusCategoryEnum.APPROVED] : this.siteHelper.allCategoryValues,
        sites: this.selection.selected,
        buttonTextYes: 'Confirm',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.queryItems();
      }
    });
  }

  onBulkPaySites(): void {
    const dialogRef = this.dialog.open(DialogConfirmPaymentComponent, {
      panelClass: 'fc-dialog-confirm-payment',
      data: {
        line1: '',
        periods: this.siteHelper.getPayablePaymentPeriods(this.selection.selected),
        sites: this.selection.selected,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const sites = result.sites.map(p => ({
          site_id: p.id,
          period: result.payPeriod,
        }));
        this.spinnerService.show('app-spinner', true, 'Making Payments');
        this.paymentService.paySites(sites).subscribe(resp => {
          this.spinnerService.hide('app-spinner');
          this.displayPaymentResults(resp, this.dialog);
          this.queryItems();
        });
      }
    });
  }

  onBulkStripeEmailSend(): void {
    const userIds = uniq(this.selection.selected.map(site => site.account.user.id));
    const siteIds = uniq(this.selection.selected.map(site => site.id));
    const dialogRef = this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'SEND EMAIL',
        line1: `You are about to send an email to ${userIds.length} people to set up their Stripe account. Please confirm.`,
        buttonTextYes: 'Confirm',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.paymentService.sendStripeEmails(siteIds).subscribe(() => {
          this.selection.clear();
          this.enableBulkStripeEmailButton = false;
        });
      }
    });
  }

  clickRow(row: Site): void {
    this.router.navigate(['/admin/site', row.id]);
  }

  clearFilters(): void {
    if (this.getBaseQueryString() !== this.getFullQueryString()) {
      super.clearFilters();
      this.ownerQuery = '';
      this.statusQuery = '';
      this.hasStripeQuery = '';
      this.needsReviewQuery = '';
      this.delegateQuery = '';
      this.regionQuery = '';
      this.showTerminatedQuery = '';
      this.showAssignedToMeQuery = '';
      this.filterValues = {
        name: '',
        id: '',
        owner: '',
        delegate: '',
        status: [],
        cohort_id: null,
        region: null,
        show_terminated: false,
        assigned_to_me: false,
        user_has_stripe: null,
        buyer: null,
        assigned_admin_id: null,
        needs_review: null,
      };
      this.queryItems();
    }
  }

  clearSelection(): void {
    super.clearSelection();
    this.enableBulkSiteStateChangeButton = false;
    this.enableBulkPaySiteButton = false;
    this.enableBulkStripeEmailButton = false;
  }

  getValuationDisplay(site: Site): string {
    const currentValuationRange = site.getAverageAnnualRevenue();
    return this.formatService.formatMinMaxValuation(currentValuationRange);
  }

  bulkAction($event: MatSelectChange) {
    switch ($event.value) {
      case 'pay':
        this.onBulkPaySites();
        break;
      case 'csv':
        this.onBulkCsvExport();
        break;
      case 'stripe':
        this.onBulkStripeEmailSend();
        break;
      case 'status':
        this.onBulkChangeSiteState();
        break;
      default:
        console.log('No Action found');
    }
    $event.source.value = '';
  }

  updateDashboardView() {
    let newDashboard = this.dashboards.find(d => {
      return d.id == this.currentDashboardId;
    });
    if (newDashboard) {
      this.currentDashboard = newDashboard;
      this.displayedColumns = newDashboard.columns;
      this.store.dispatch(
        setDashboardState({
          state: newDashboard.defaultState,
          page: this.PAGE_NAME,
        })
      );
      this.resetAdminFilters([newDashboard.defaultState]);
      this.queryItems();
    }
  }

  reorderColumns($event: any) {
    moveItemInArray(this.displayedColumns, $event.previousIndex, $event.currentIndex);
  }

  removeColumn(columnName: string) {
    this.displayedColumns = this.displayedColumns.filter(col => col !== columnName);
  }

  toggleEditColumns() {
    this.editMode = !this.editMode;
  }

  deleteDashboardView() {
    if (this.currentDashboard?.ownerId != null) {
      this.dialog.open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Delete View',
          line1: 'Are you sure you want to delete this view?',
          buttonTextYes: 'Delete',
          buttonTextNo: 'Cancel',
        },
        autoFocus: false,
      }).afterClosed().subscribe(result => {
        if (result) {
          this.dashboardService.delete(this.currentDashboard.id).subscribe((dashboards: Dashboard[]) => {
            this.dashboards = dashboards;
            this.currentDashboard = null;
            this.currentDashboardId = null;
            this.setDefaultDashboard();
            this.updateDashboardView();
          });
        }
      });
    }
  }

  openSaveView() {
    this.dialog.open(DialogChangeDashboardNameComponent, {
      data: {
        line1:
          this.currentDashboard?.ownerId != null ?
            'Do you want to save this as a new dashboard or update the current one?'
            : 'This is a default Dashboard. Please enter a new name to customize your view.',
        currentId: this.currentDashboard?.ownerId != null ? this.currentDashboard.id : null,
        currentName: this.currentDashboard?.ownerId != null ? this.currentDashboard.name : 'New Dashboard',
        isDefault: this.currentDashboard?.ownerId != null ? this.currentDashboard.isDefault : false,
      },
    }).afterClosed().subscribe(result => {
      if (result) {
        this.store.select(selectAdminFiltersAndPage).pipe(take(1)).subscribe(state => {
          let filters = state.filters.find(f => f.page === this.PAGE_NAME);
          let newDashboard = {
            type: 'sites',
            name: result.name,
            columns: this.displayedColumns,
            defaultState: filters,
            isDefault: result.isDefault
          }
          if (result.id) {
            //save existing
            this.dashboardService.update(result.id, newDashboard).subscribe((dashboard: Dashboard) => {
              this.dashboards.splice(this.dashboards.indexOf(this.currentDashboard), 1, dashboard)
            });
          } else {
            // create new
            this.dashboardService.createNew(newDashboard).subscribe((dashboard: Dashboard) => {
              this.dashboardService.getDashboards(this.PAGE_NAME).subscribe((dashboards: Dashboard[]) => {
                this.dashboards = dashboards;
              });
              this.currentDashboardId = dashboard.id;
              this.currentDashboard = dashboard;
            });
          }
        });
      }
    });
  }

  addColumn($event: MatSelectChange) {
    this.displayedColumns = this.displayedColumns.filter(x => $event.value.includes(x));
    let toAdd = $event.value.filter(x => !this.displayedColumns.includes(x));
    this.displayedColumns.unshift(...toAdd);
  }
}
